import { useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PageHeader, SectionCard, StatCard } from '@/components/admin/page';
import { api, ApiError } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { useI18n } from '../../lib/i18n';

interface MetricsResponse {
  timestamp: string;
  users: { total: number; verified: number; verifiedPct: number };
  activity: { dau: number; wau: number; mau: number };
  tenants: number;
  employees: number;
  automations: number;
  conversations: number;
  executions: number;
  funnel: { signed_up: number; verified: number; logged_in_once: number };
}

export function MetricsPage() {
  const { t } = useI18n();
  const [data, setData] = useState<MetricsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const m = await api<MetricsResponse>('/v1/admin/metrics');
        setData(m);
        setError(null);
      } catch (e) {
        setError(e instanceof ApiError ? e.message : (e as Error).message);
      } finally {
        setLoading(false);
      }
    };
    void load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, []);

  if (loading) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;
  if (error) return <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>;
  if (!data) return null;

  return (
    <div>
      <PageHeader
        title={t('metrics.title')}
        description={t('metrics.desc', { ts: formatDateTime(data.timestamp) })}
      />

      <div className="space-y-6">
        <SectionCard title={t('metrics.section.active')} description={t('metrics.section.activeDesc')}>
          <div className="grid admin-grid gap-3">
            <StatCard label={t('metrics.dau')} value={data.activity.dau} sub={t('metrics.dauSub')} />
            <StatCard label={t('metrics.wau')} value={data.activity.wau} sub={t('metrics.wauSub')} />
            <StatCard label={t('metrics.mau')} value={data.activity.mau} sub={t('metrics.mauSub')} />
          </div>
        </SectionCard>

        <SectionCard title={t('metrics.section.account')} description={t('metrics.section.accountDesc')}>
          <div className="grid admin-grid gap-3">
            <StatCard label={t('metrics.totalAccounts')} value={data.funnel.signed_up} />
            <StatCard label={t('metrics.verified')} value={data.funnel.verified} sub={`${data.users.verifiedPct}%`} />
            <StatCard label={t('metrics.firstLogin')} value={data.funnel.logged_in_once} />
          </div>
        </SectionCard>

        <SectionCard title={t('metrics.section.service')} description={t('metrics.section.serviceDesc')}>
          <div className="grid admin-grid gap-3">
            <StatCard label={t('metrics.workspaces')} value={data.tenants} />
            <StatCard label={t('metrics.employees')} value={data.employees} />
            <StatCard label={t('metrics.automations')} value={data.automations} />
            <StatCard label={t('metrics.conversations')} value={data.conversations} />
            <StatCard label={t('metrics.executions')} value={data.executions} />
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
